export interface MemberAmounts {
  JR: number
  KB: number
  LVW: number
  TR: number
  SM: number
}

export interface TierBreakdown {
  t1: MemberAmounts
  hp: MemberAmounts
  s3a: MemberAmounts
  s3b: MemberAmounts
  s4a: MemberAmounts
  s4b: MemberAmounts
}

export type WithheldTiers = { s3b: MemberAmounts; s4b: MemberAmounts }

export type ProposalVestingItem = {
  member: MemberCode
  prior_payable: number
  this_distribution: number
  pro_forma_payable: number
  withheld_balance: number
  from_new_cash: number
  last_pool_event_num: number | null
  last_pool_event_date: string | null
}

export interface ProposeRequest {
  date: string
  amount: number
  mode?: 'sheet' | 'no_trueups'
  catch_up_addons?: VestingCatchUpAddon[]
}

export interface TraceStep1 {
  step: string
  cash_available_entering?: number
  target_cum_thru_last?: MemberAmounts
  actual_cum_thru_last?: MemberAmounts
  true_up?: MemberAmounts
  true_up_paid_now?: MemberAmounts
  stub_days_since_last?: number
  stub_accrual?: MemberAmounts
  pay_now?: MemberAmounts
  remaining_after?: number
}

export interface TraceStep2 {
  step: string
  cash_available_entering?: number
  hp_threshold_balance_before?: number
  target_cum_2x_t1?: MemberAmounts
  actual_cum_thru_last?: MemberAmounts
  true_up?: MemberAmounts
  true_up_paid_now?: MemberAmounts
  new_repayment_2x_this_t1?: MemberAmounts
  pay_now?: MemberAmounts
  remaining_after?: number
}

export interface TraceStep3 {
  step: string
  cash_available_entering?: number
  threshold_after_step2?: number
  cum_pool_target_thru_last?: number
  classA_target?: MemberAmounts
  classA_actual?: MemberAmounts
  classA_true_up?: MemberAmounts
  classB_target?: MemberAmounts
  classB_actual?: MemberAmounts
  classB_true_up?: MemberAmounts
  pool_this_distribution?: number
  classA_flow?: MemberAmounts
  classB_flow?: MemberAmounts
  pay_now_A?: MemberAmounts
  pay_now_B?: MemberAmounts
  remaining_after?: number
}

export interface TraceStep4 {
  step: string
  cash_available_entering?: number
  pay_now_A?: MemberAmounts
  pay_now_B?: MemberAmounts
  remaining_after?: number
}

export type TraceStep = TraceStep1 | TraceStep2 | TraceStep3 | TraceStep4

export interface ProposalResponse {
  inputs: Record<string, unknown>
  steps: TraceStep[]
  proposal: {
    by_member: MemberAmounts
    total: number
    row_for_sheet: TierBreakdown
    withheld: WithheldTiers
    withheld_total: number
    cash_by_member: MemberAmounts
    cash_total: number
  }
  vesting: ProposalVestingItem[]
  hp_threshold: {
    balance: number
    accrued_uncapitalized: number
    economic_balance: number
  }
  warnings: string[]
  catch_up_addons?: Array<{ member: string; tier: string; amount: number }>
}

export interface DistributionCreate {
  date: string
  amount: number
  expected_last_num: number
  note?: string | null
  t1: MemberAmounts
  hp: MemberAmounts
  s3a: MemberAmounts
  s3b: MemberAmounts
  s4a: MemberAmounts
  s4b: MemberAmounts
  catch_up_addons?: VestingCatchUpAddon[]
  withheld?: WithheldTiers
}

export interface DistributionResponse {
  id: number
  num: number
  date: string
  amount: number
  note: string | null
  t1: MemberAmounts
  hp: MemberAmounts
  s3a: MemberAmounts
  s3b: MemberAmounts
  s4a: MemberAmounts
  s4b: MemberAmounts
  by_member: MemberAmounts
  total: number
  cash_total: number
  withheld_total: number
  withheld: WithheldTiers
  cash_by_member: MemberAmounts
  catch_up_by_member: MemberAmounts
  created_at: string
  updated_at: string
  warnings: string[]
  je_status?: WaterfallJeStatusValue
  je_draft_id?: number | null
  je_qbo_url?: string | null
  content_dirty?: boolean
}

export interface DistributionListResponse {
  distributions: DistributionResponse[]
  count: number
}

export interface AccrualsResponse {
  accruals: Record<string, Record<string, number>>
  rows: Record<string, unknown>[]
  count: number
  notes: string[]
}

export interface ThresholdReconciliationItem {
  date: string
  amount: number
  description: string
}

export interface ThresholdResponse {
  asof: string
  balance: {
    balance: number
    accrued_uncapitalized: number
    economic_balance: number
  }
  legacy_as_imported: {
    balance: number
    accrued_uncapitalized: number
    economic_balance: number
  } | null
  reconciliation: ThresholdReconciliationItem[]
  extra_repayments_applied: Record<string, number>
}

export interface DailyThresholdRow {
  date: string
  prime_rate: number
  opening_balance: number
  issuance: number
  repayment: number
  daily_interest: number
  accrued_uncapitalized: number
  capitalized: boolean
  closing_balance: number
  instrument?: string
  memo?: string
}

export interface VestingStep {
  date: string
  units: number
  cumulative_pct: number
  cash_pct?: number
  status: 'completed' | 'current' | 'upcoming'
  paid_catch_up: number
  payable_now: number | null
  deferred_unvested: number | null
  total_payable_at_vest: number | null
  target_at_current_entitlement?: number | null
}

export interface VestingAccount {
  member: string
  grant_date: string
  total_units: number
  vested_cash_pct: number
  vested_units: number
  cumulative_entitlement: number
  cumulative_paid: number
  cumulative_ordinary_paid: number
  cumulative_catch_up_paid: number
  vested_target: number
  payable_now: number
  payable_s3b: number
  payable_s4b: number
  deferred_unvested: number
  paid_ahead: number
  next_vest_date: string | null
  next_vest_cash_pct: number
  next_vest_units: number
  payable_at_next_vest: number
  latest_history_num: number | null
  redistribution_recipients: string[]
  cumulative_withheld: number
  withheld_balance: number
  payable_from_new_cash: number
  last_pool_event_num: number | null
  last_pool_event_date: string | null
}

export interface VestingSection {
  member: string
  grant_date: string
  total_units: number
  account: VestingAccount | null
  steps: VestingStep[]
}

export interface VestingCatchUpAddon {
  member: 'JR' | 'KB' | 'LVW' | 'TR' | 'SM'
  amount: number
  note?: string | null
}

export interface VestingCatchUpCreate {
  date: string
  member: 'JR' | 'KB' | 'LVW' | 'TR' | 'SM'
  amount: number
  expected_last_num: number
  note?: string | null
}

export interface VestingResponse {
  asof: string
  total_sm_units: number
  class_b_units: Record<string, number>
  vested_pct: number
  steps: VestingStep[]
  accounts: VestingAccount[]
  sections: VestingSection[]
}

export interface DailyThresholdResponse {
  start: string
  end: string
  rows: DailyThresholdRow[]
  count: number
  summary: {
    balance: number
    accrued_uncapitalized: number
    economic_balance: number
  }
}

export interface ProjectedThresholdResponse {
  case_id: number | null
  case_name: string | null
  splice_date: string | null
  end: string | null
  rows: DailyThresholdRow[]
  reason: string | null
}

export interface WaterfallConfigItem {
  key: string
  value: unknown
  description?: string | null
  updated_at?: string | null
}

export interface WaterfallSettingsResponse {
  items: WaterfallConfigItem[]
  prime_rate_last_synced_at?: string | null
  capital_contributions_last_synced_at?: string | null
  warnings?: string[]
}

export interface CapitalContributionsSyncDiffRow {
  date: string
  member: string
  current: number
  proposed: number
  delta: number
  ignored?: boolean
}

export interface CapitalContributionsSyncPreviewResponse {
  proposed: CapitalContributionsConfig
  current: CapitalContributionsConfig
  diff: CapitalContributionsSyncDiffRow[]
  totals: Record<string, number>
  warnings: string[]
  logged_allocation_warning: string | null
}

export interface EquityTieOutResponse {
  as_of: string
  engine_total_equity: number
  qbo_total_equity: number
  difference: number
}

export interface PrimeRateSyncResponse {
  added: Array<{ effective: string; rate: number }>
  current_rate: number | null
  last_synced_at: string
  source: string
}

export interface WaterfallAgreementMeta {
  is_custom: boolean
  filename: string
  original_filename: string
  content_type: string
  format: 'docx' | 'pdf'
  uploaded_at: string | null
}

export interface ObligationsResponse {
  asof: string
  next_distribution_num: number
  last_distribution_num: number
  last_distribution_date: string
  t1_owed: number
  hp_owed: number
  minimum_to_clear: number
  accruals_net_outstanding: number
  accruals_t1_hp_outstanding: number
  accruals_t3_net: number
  stub_t1_through_asof: number
  stub_hp_through_asof: number
  projected_accruals_net: number
}

export interface CapTableClassTotals {
  granted: number
  vested: number
}

export interface CapTableRow {
  member: MemberCode
  unitholder: string
  class: 'A' | 'B' | 'C'
  grant_date: string | null
  granted_units: number
  vested_units: number
  granted_pct_of_class: number
  vested_pct_of_class: number
  granted_pct_of_a_b: number | null
  vested_pct_of_a_b: number | null
  granted_pct_of_a_c: number | null
  vested_pct_of_a_c: number | null
}

export interface CapTableTotals {
  class_a: CapTableClassTotals
  class_b: CapTableClassTotals
  class_c: CapTableClassTotals
  class_a_b: CapTableClassTotals
  class_a_c: CapTableClassTotals
}

export interface CapTableResponse {
  asof: string
  vested_basis_note: string
  totals: CapTableTotals
  rows: CapTableRow[]
}

export const MEMBERS = ['JR', 'KB', 'LVW', 'TR', 'SM'] as const
export type MemberCode = (typeof MEMBERS)[number]

export const MEMBER_LABELS: Record<MemberCode, string> = {
  JR: 'Julie & Edward',
  KB: 'Brittany & Keller',
  LVW: 'Lauren & David',
  TR: 'Alicia & Taylor',
  SM: 'Scott',
}

export const MEMBER_COLORS: Record<MemberCode, string> = {
  JR: '#6366f1',
  KB: '#8b5cf6',
  LVW: '#0ea5e9',
  TR: '#14b8a6',
  SM: '#f59e0b',
}

export const TIER_LABELS: Record<keyof TierBreakdown, string> = {
  t1: 'Tier 1',
  hp: 'Tier 2 (HP)',
  s3a: 'Tier 3A',
  s3b: 'Tier 3B',
  s4a: 'Tier 4A',
  s4b: 'Tier 4B',
}

export const MODE_LABELS: Record<'sheet' | 'no_trueups', string> = {
  sheet: 'Apply accrual corrections',
  no_trueups: 'Current-period allocation only',
}

export const MODE_HELP: Record<'sheet' | 'no_trueups', string> = {
  sheet:
    'Within each tier, first use available cash to correct outstanding accruals from prior distributions, then allocate the remainder for this distribution.',
  no_trueups:
    'Ignore outstanding accruals from prior distributions. Allocate this distribution based on current-period entitlements only.',
}

export interface AllocationProposeRequest {
  period_start: string
  period_end: string
  net_income?: number
  note?: string | null
}

export interface AllocationProposalResponse {
  period_start: string
  period_end: string
  net_income: number
  method_note: string
  equity_begin: number
  equity_end: number
  beginning_capital: MemberAmounts
  target_capital: MemberAmounts
  ending_capital: MemberAmounts
  allocation: MemberAmounts
  contributions: MemberAmounts
  distributions: MemberAmounts
  transfers: Record<string, number>
  residual_plug: number
  trace: Record<string, unknown>
  warnings: string[]
}

export interface AllocationCreate {
  period_start: string
  period_end: string
  net_income: number
  expected_last_num: number
  note?: string | null
  allocation: MemberAmounts
}

export interface AllocationResponse {
  id: number
  num: number
  period_start: string
  period_end: string
  net_income: number
  note: string | null
  allocation: MemberAmounts
  beginning_capital: MemberAmounts
  target_capital: MemberAmounts
  ending_capital: MemberAmounts
  contributions: MemberAmounts
  distributions: MemberAmounts
  transfers: Record<string, number> | null
  equity_end: number
  created_at: string
  updated_at: string
  warnings: string[]
  je_status?: WaterfallJeStatusValue
  je_draft_id?: number | null
  je_qbo_url?: string | null
  content_dirty?: boolean
}

export interface AllocationListResponse {
  allocations: AllocationResponse[]
  count: number
}

export interface CapitalAccountMember {
  member: MemberCode
  seed_amount: number
  join_date: string | null
  cumulative_contributions: number
  cumulative_transfers: number
  cumulative_distributions: number
  cumulative_allocations: number
  current_capital: number
  current_target: number
  drift: number
}

export interface CapitalAccountsResponse {
  asof: string
  method_note: string
  total_equity: number
  members: CapitalAccountMember[]
}

export interface CapitalSeedMember {
  /** Always 0 under join-date seeding; capital starts at zero on join. */
  amount?: number
  join_date: string
}

export interface CapitalAccountSeed {
  /** Day before earliest join date (server-derived). */
  asof?: string
  total_equity?: number
  source?: string
  by_member: Partial<Record<MemberCode, CapitalSeedMember>>
}

/** capital_contributions config: date → member → amount */
export type CapitalContributionsConfig = Record<string, Partial<Record<MemberCode, number>>>

export interface CapitalTransfer {
  from: MemberCode
  to: MemberCode
  amount: number
}

/** capital_transfers config: date → list of member-to-member transfers */
export type CapitalTransfersConfig = Record<string, CapitalTransfer[]>

/** preferred_ownership config: date → member → share (0–1) */
export type PreferredOwnershipConfig = Record<string, Partial<Record<MemberCode, number | string>>>

export type WaterfallJeMode = 'cash' | 'income'

export type WaterfallJeStatusValue = 'none' | 'draft' | 'posted' | 'stale'

export interface WaterfallJournalEntryLine {
  account_id: string
  account_name: string
  posting_type: 'Debit' | 'Credit'
  amount: number | string
  description?: string
  department_id?: string | null
  department_name?: string | null
}

export interface WaterfallJournalEntryDraftItem {
  id: number
  tool: string
  status: string
  txn_date: string
  doc_number: string
  lines: WaterfallJournalEntryLine[]
  total_debits: number | string
  total_credits: number | string
  warnings?: string[]
  source_data?: Record<string, unknown> | null
  private_note?: string | null
  qbo_entity_id?: string | null
  qbo_url?: string | null
  error_message?: string | null
  created_at: string
  posted_at?: string | null
}

export interface WaterfallJournalEntryStatus {
  mode: WaterfallJeMode
  report_month: string
  status: WaterfallJeStatusValue
  doc_number: string
  distribution_num?: number | null
  content_dirty: boolean
  needs_qbo_update: boolean
  draft: WaterfallJournalEntryDraftItem | null
}

export interface WaterfallJournalEntryGenerateRequest {
  mode: WaterfallJeMode
  month?: string
  distribution_num?: number
  overwrite_posted?: boolean
}

export interface WaterfallJournalEntryGenerateResponse {
  draft: WaterfallJournalEntryDraftItem
  overwritten: boolean
  needs_qbo_update: boolean
}

export interface WaterfallPushQboUpdateRequest {
  force_regenerate?: boolean
  regenerate_if_dirty?: boolean
}

export interface WaterfallPushQboUpdateResponse {
  draft: WaterfallJournalEntryDraftItem
  regenerated: boolean
}

export interface WaterfallDeleteQboResponse {
  draft: WaterfallJournalEntryDraftItem
}

export interface PacYearEndCloseStatusResponse {
  month: string
  prior_year: number
  as_of: string
  txn_date: string
  draft: WaterfallJournalEntryDraftItem | null
}

export interface PacYearEndCloseGenerateRequest {
  month: string
  overwrite_posted?: boolean
}

export interface PacYearEndCloseGenerateResponse {
  draft: WaterfallJournalEntryDraftItem
  overwritten: boolean
  needs_qbo_update: boolean
}

export interface WaterfallJeMonthsSummaryMonth {
  report_month: string
  je_status: WaterfallJeStatusValue
  checklist_complete: boolean | null
  can_post_je: boolean | null
  checked_count: number | null
}

export interface WaterfallJeMonthsSummaryResponse {
  mode: WaterfallJeMode
  months: WaterfallJeMonthsSummaryMonth[]
}

export interface AllocationMonthReadinessItem {
  id: string
  label: string
  required: boolean
  blocks_je_post?: boolean
  checked: boolean
  checked_at?: string | null
  checked_by?: string | null
}

export interface AllocationMonthReadinessResponse {
  report_month: string
  items: AllocationMonthReadinessItem[]
  checked_count: number
  required_count: number
  all_required_checked: boolean
  logged_net_income: number | null
  qbo_net_income: number | null
  net_income_drift: boolean
  allocations_after_count: number
  can_post_je: boolean
  notes: string
}

export interface AllocationMonthReadinessUpdateRequest {
  month: string
  check_id: string
  checked: boolean
}

export interface AllocationMonthReadinessNotesUpdateRequest {
  month: string
  notes: string
}

export interface NetIncomeRangeDriftMismatch {
  report_month: string
  month_label: string
  booked_net_income: number
  qbo_net_income: number
  difference: number
}

export interface NetIncomeRangeDriftResponse {
  start_month: string
  end_month: string
  qbo_available: boolean
  mismatches: NetIncomeRangeDriftMismatch[]
}

export interface JeReadinessBlockedDetail {
  message: string
  checklist_incomplete: boolean
  net_income_drift: boolean
}
